/**
 * Coordinate model for pins on the aircraft drawings.
 *
 * Positions are stored normalised to the drawing IMAGE (not to the board it is
 * displayed in): origin top-left, x and y in [0, 1], four decimals. Two things
 * follow from that and both are easy to get wrong, so they live here with tests:
 *
 * 1. Letterboxing. The image is laid into a 3:2 board with object-fit: contain,
 *    so there is empty space either left/right or top/bottom. A tap on the
 *    board must be mapped through the image's actual rectangle, otherwise pins
 *    drift between a phone and a desktop.
 *
 * 2. Aspect ratio. Because x is normalised to the width and y to the height,
 *    one unit of y is not one unit of x. A "radius" of 0.085 compared against
 *    sqrt(dx² + dy²) is an ellipse. The nearby search scales dy by the image's
 *    height/width ratio first. With a square test image this bug is invisible.
 *
 * Mirrored views (unten/rechts) are rendered from the base image flipped
 * horizontally. Mirroring is a RENDERING concern only: the stored coordinate is
 * in the frame of the view as it appears on screen (x = 0 is the viewer's left
 * when looking at the right side). That way stored pins stay valid if a type
 * later gets its own, unmirrored image for that view, and the database never
 * knows about mirroring. The board flips the image (CSS scaleX(-1)); taps and
 * pins are mapped without any flip.
 *
 * `innen` (the cabin from above, nose up) is a view of its own: never
 * mirrored and never derived. A type without an interior image is shown with
 * a built-in cabin schematic of CABIN_PLACEHOLDER_SIZE (drawings.ts).
 */

export type AircraftView = 'oben' | 'unten' | 'links' | 'rechts' | 'vorne' | 'hinten' | 'innen';

/** The cabin interior: its own image or the cabin placeholder, never a mirror of another view. */
export const CABIN_VIEW = 'innen' as const;

export const BASE_VIEWS = ['oben', 'vorne', 'links'] as const;
export type BaseView = (typeof BASE_VIEWS)[number];

/**
 * Default derivation of the mirrored views; per-type overrides live in
 * aircraft_types.mirror_json. No „hinten“ since 09.10.2026 (drawings.ts, ALL_VIEWS).
 */
export const DEFAULT_MIRROR_RULES: Readonly<Partial<Record<Exclude<AircraftView, BaseView | typeof CABIN_VIEW>, BaseView>>> = {
  unten: 'oben',
  rechts: 'links',
};

export interface NormalizedPoint {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function isBaseView(view: AircraftView): view is BaseView {
  return (BASE_VIEWS as readonly string[]).includes(view);
}

/**
 * Resolves which base image a view is drawn from and whether it is mirrored.
 * `rules` comes from aircraft_types.mirror_json; a type may supply its own
 * image for a derived view by omitting it from the rules.
 */
export function resolveView(
  view: AircraftView,
  rules: Partial<Record<AircraftView, BaseView>> = DEFAULT_MIRROR_RULES,
): { base: AircraftView; mirrored: boolean } {
  if (isBaseView(view) || view === CABIN_VIEW) return { base: view, mirrored: false };
  const base = rules[view];
  return base ? { base, mirrored: true } : { base: view, mirrored: false };
}

/**
 * Where the reporter stands. Top and bottom views are drawn nose up — right
 * for somebody behind the tail, upside down for somebody at the nose, who
 * would then put a pin on the wrong wing (owner, 17.10.2026). `nose` shows
 * the drawing turned by 180°.
 *
 * Like mirroring, the turn is a RENDERING concern only: what is stored stays
 * in the frame of the unturned view (module comment), so the API, the
 * protocol and every other screen never learn where somebody stood.
 */
export type Standpoint = 'tail' | 'nose';

/** The views that ask "Wo stehst du?"; side views look the same from both ends. */
export const STANDPOINT_VIEWS: readonly AircraftView[] = ['oben', 'unten'];

export function asksStandpoint(view: AircraftView): boolean {
  return STANDPOINT_VIEWS.includes(view);
}

export function isTurned(view: AircraftView, standpoint: Standpoint | null | undefined): boolean {
  return standpoint === 'nose' && asksStandpoint(view);
}

/**
 * Between the stored frame and the screen of a turned view: a turn by 180°
 * is (x, y) → (1 − x, 1 − y). It is its own inverse, so the same call maps
 * a stored pin onto the screen and a tap on the screen back to what is stored.
 */
export function turnPoint<T extends NormalizedPoint>(point: T, turned: boolean): T {
  if (!turned) return point;
  return { ...point, x: roundCoordinate(1 - point.x), y: roundCoordinate(1 - point.y) };
}

/**
 * How the base IMAGE is flipped on screen: the horizontal mirror of a derived
 * view, then the turn. A turn by 180° is both axes flipped, so on a mirrored
 * view (unten) the two horizontal flips cancel and only the vertical one is
 * left. Web: `scale(scaleX, scaleY)`; mobile: `[{ scaleX }, { scaleY }]`.
 */
export function imageFlip(mirrored: boolean, turned: boolean): { scaleX: 1 | -1; scaleY: 1 | -1 } {
  return { scaleX: mirrored !== turned ? -1 : 1, scaleY: turned ? -1 : 1 };
}

/** Rectangle the image occupies inside a board when rendered with object-fit: contain. */
export function containRect(image: Size, board: Size): Rect {
  if (image.width <= 0 || image.height <= 0 || board.width <= 0 || board.height <= 0) {
    throw new RangeError('containRect needs positive sizes');
  }
  const scale = Math.min(board.width / image.width, board.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    left: (board.width - width) / 2,
    top: (board.height - height) / 2,
    width,
    height,
  };
}

/** Rounds to the four decimals the database stores, so client and server agree on equality. */
export function roundCoordinate(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

/**
 * Maps a tap at board pixel coordinates to a normalised coordinate of the view.
 * Returns null when the tap landed in the letterbox outside the image.
 * No mirroring here on purpose: see the module comment.
 */
export function boardPointToNormalized(
  boardX: number,
  boardY: number,
  image: Size,
  board: Size,
): NormalizedPoint | null {
  const rect = containRect(image, board);
  const x = (boardX - rect.left) / rect.width;
  const y = (boardY - rect.top) / rect.height;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return { x: roundCoordinate(x), y: roundCoordinate(y) };
}

/** Inverse of boardPointToNormalized: where to draw a stored pin on this board. */
export function normalizedToBoardPoint(
  point: NormalizedPoint,
  image: Size,
  board: Size,
): { x: number; y: number } {
  const rect = containRect(image, board);
  return { x: rect.left + point.x * rect.width, y: rect.top + point.y * rect.height };
}

/**
 * Distance between two normalised points in units of the image WIDTH, so that
 * a radius expressed as a fraction of the width describes a circle on screen.
 * `aspect` is height / width of the drawing (aircraft_types.aspect_json[view]).
 */
export function normalizedDistance(a: NormalizedPoint, b: NormalizedPoint, aspect: number): number {
  if (!(aspect > 0)) throw new RangeError('aspect (height/width) must be positive');
  const dx = a.x - b.x;
  const dy = (a.y - b.y) * aspect;
  return Math.hypot(dx, dy);
}

export interface Positioned extends NormalizedPoint {
  id: string;
}

export interface NearbyHit<T extends Positioned> {
  item: T;
  distance: number;
}

/**
 * Candidates within `radius` (fraction of image width) of `point`, nearest first.
 * Used identically by the web client (before saving), the mobile client
 * (offline, against its local copy) and the API (server-side validation), so
 * that all three agree on what "already known nearby" means.
 */
export function findNearby<T extends Positioned>(
  point: NormalizedPoint,
  candidates: readonly T[],
  radius: number,
  aspect: number,
): NearbyHit<T>[] {
  if (!(radius >= 0)) throw new RangeError('radius must be >= 0');
  return candidates
    .map((item) => ({ item, distance: normalizedDistance(point, item, aspect) }))
    .filter((hit) => hit.distance < radius)
    .sort((p, q) => p.distance - q.distance);
}

/** Groups pins closer than `threshold` (fraction of width) into clusters for display. */
export function clusterPins<T extends Positioned>(
  pins: readonly T[],
  threshold: number,
  aspect: number,
): T[][] {
  const clusters: T[][] = [];
  for (const pin of pins) {
    const target = clusters.find((cluster) =>
      cluster.some((member) => normalizedDistance(member, pin, aspect) < threshold),
    );
    if (target) target.push(pin);
    else clusters.push([pin]);
  }
  return clusters;
}
